import { webApi } from '@mi-front/api-clients';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRedirectToVideoEditorEdit } from '../../hooks/useVideoEditorEditUrl';

interface CloneVideoEditionEditDialogProps {
  open: boolean;
  onClose: () => void;
  sourceEdit: webApi.EnrichedVideoEditorEditListItemDto;
  onSuccess?: () => Promise<void> | void;
}

/**
 * Dialog to clone an existing video edition edit with a new name.
 */
export function CloneVideoEditionEditDialog({
  open,
  onClose,
  sourceEdit,
  onSuccess,
}: CloneVideoEditionEditDialogProps) {
  const { $t } = useIntl();
  const redirectToVideoEditorEdit = useRedirectToVideoEditorEdit();
  const cloneMutation = webApi.useCloneEditVideoEditorEdits({
    mutation: {
      onSuccess: async (created) => {
        await onSuccess?.();
        redirectToVideoEditorEdit(created.id);
        onClose();
      },
    },
  });

  const defaultName = `${sourceEdit.name} (copia)`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.elements.namedItem('name') as HTMLInputElement | null;
    const name = input?.value?.trim() || defaultName;
    cloneMutation.mutate({
      id: sourceEdit.id,
      data: { name },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="videoEditions.cloneDialog.title" defaultMessage="Clonar edit" />
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            name="name"
            label={$t({
              id: 'videoEditions.cloneDialog.nameLabel',
              defaultMessage: 'Nombre',
            })}
            placeholder={defaultName}
            defaultValue={defaultName}
            fullWidth
            required
            autoFocus
            error={!!cloneMutation.error}
            helperText={cloneMutation.error ? (cloneMutation.error as Error).message : undefined}
            disabled={cloneMutation.isPending}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={cloneMutation.isPending}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
          </Button>
          <Button type="submit" variant="contained" disabled={cloneMutation.isPending}>
            <FormattedMessage id="videoEditions.cloneDialog.submit" defaultMessage="Clonar" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
